import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SlidersHorizontal } from "lucide-react";
import { useController } from "react-hook-form";
import {
  BOX_SHADOW_FIELDS,
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";
import { getReactHookFormNestedError } from "../../../utils/errors";
import {
  emitPxMetricChange,
  isIncompletePxDraft,
  parsePxMetricInput,
} from "./pxMetricInput";
import { useSliderDragCommit } from "./useSliderDragCommit";

const optionalRules = { required: false };
const SLIDER_THUMB_SIZE = 20;
const SLIDER_STEP = 1;
const SLIDER_MIN = 0;

const sliderSx = {
  flex: 1,
  minWidth: 0,
  ml: 0,
  color: "#000",
  ".MuiSlider-track": {
    backgroundColor: "#000",
    height: 4,
  },
  ".MuiSlider-rail": {
    backgroundColor: "rgba(0,0,0,0.5)",
    height: 4,
  },
  ".MuiSlider-mark": {
    backgroundColor: "transparent",
  },
  ".MuiSlider-thumb": {
    width: SLIDER_THUMB_SIZE,
    height: SLIDER_THUMB_SIZE,
    border: "1px solid #e1e1e1",
    backgroundColor: "#fff",
  },
};

const numberInputSx = {
  width: 64,
  flexShrink: 0,
  height: 36,
  px: 1,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  borderRadius: "10px",
  bgcolor: "fields.background",
  boxShadow: BOX_SHADOW_FIELDS,
  outline: "none",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 500,
  color: "#000",
  textAlign: "center",
  appearance: "none",
  "&:disabled": {
    color: "text.disabled",
  },
};

const tooltipSlotSx = {
  p: 2,
  maxWidth: "none",
  width: 320,
  backgroundColor: "background.paper",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: BOX_SHADOW_THEME,
  borderRadius: BORDER_RADIUS,
  color: "text.primary",
  fontSize: 14,
  mt: "8px !important",
};

const numericValue = (value) => {
  const parsed = parsePxMetricInput(value);
  return parsed.kind === "number" ? parsed.value : null;
};

const MetricSliderRow = ({
  control,
  errors,
  name,
  label,
  disabled = false,
  sliderMax,
  onChangeField,
  registerSetter,
}) => {
  const { field } = useController({
    control,
    name,
    rules: optionalRules,
  });
  const { onChange: setFieldValue } = field;
  useEffect(() => {
    registerSetter?.(name, setFieldValue);
  }, [registerSetter, name, setFieldValue]);
  const error = getReactHookFormNestedError(errors, name);
  const onChangeFieldRef = useRef(onChangeField);
  onChangeFieldRef.current = onChangeField;

  const persist = useCallback(
    (value) => {
      emitPxMetricChange({
        key: name,
        value,
        onChangeField: onChangeFieldRef.current,
      });
    },
    [name]
  );

  const commit = useCallback(
    (next) => {
      field.onChange(next);
      persist(next === "" || next == null ? "" : next);
    },
    [field, persist]
  );

  const drag = useSliderDragCommit(commit);

  const parsed = numericValue(field.value);
  const sliderValue = Math.min(
    sliderMax,
    Math.max(SLIDER_MIN, parsed == null ? 0 : parsed)
  );
  const displayValue =
    field.value === "" || field.value == null ? "" : field.value;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography
        component="label"
        htmlFor={name}
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Slider
          min={SLIDER_MIN}
          max={sliderMax}
          step={SLIDER_STEP}
          value={drag.dragValue ?? sliderValue}
          disabled={disabled}
          aria-label={label}
          onChange={(_event, value) => {
            const raw = Array.isArray(value) ? value[0] : value;
            drag.onChange(typeof raw === "number" ? raw : sliderValue);
          }}
          onChangeCommitted={drag.onChangeCommitted}
          sx={sliderSx}
        />
        <Box
          component="input"
          id={name}
          name={name}
          type="text"
          inputMode="decimal"
          disabled={disabled}
          value={displayValue}
          aria-label={label}
          aria-invalid={Boolean(error)}
          autoComplete="off"
          spellCheck={false}
          placeholder="0"
          onChange={(event) => {
            const raw = event.target.value;
            const parsedInput = parsePxMetricInput(raw);
            if (parsedInput.kind === "invalid") return;
            field.onChange(raw);
            if (parsedInput.kind === "draft") return;
            persist(raw);
          }}
          onBlur={() => {
            field.onBlur();
            if (isIncompletePxDraft(field.value)) {
              commit(parsed == null ? "" : parsed);
              return;
            }
            const next = parsePxMetricInput(field.value);
            if (next.kind === "number") commit(next.value);
            else if (next.kind === "empty") commit("");
            else commit(parsed == null ? "" : parsed);
          }}
          onKeyDown={(event) => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
            event.preventDefault();
            const base = parsed == null ? 0 : parsed;
            const direction = event.key === "ArrowUp" ? 1 : -1;
            commit(Math.max(SLIDER_MIN, base + direction * SLIDER_STEP));
          }}
          sx={{
            ...numberInputSx,
            outline: error ? "1px solid" : "none",
            outlineColor: "error.main",
          }}
        />
      </Box>
    </Box>
  );
};

const PaintMetricSettings = ({
  control,
  errors,
  disabled = false,
  ariaLabel,
  fields = [],
  presets = [],
  presetsLabel = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const settersRef = useRef({});
  const registerSetter = useCallback((fieldName, setter) => {
    settersRef.current[fieldName] = setter;
  }, []);

  const applyPreset = (preset) => {
    fields.forEach((item) => {
      if (!(item.name in preset.values)) return;
      const value = preset.values[item.name];
      settersRef.current[item.name]?.(value);
      item.onChangeField?.({ key: item.name, value });
    });
  };

  const closeTooltip = () => {
    if (!isOpen) return;
    setIsOpen(false);
  };

  const toggleTooltip = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (event.target.closest?.("[data-paint-metric-tooltip]")) return;
        closeTooltip();
      }}
    >
      <Box sx={{ display: "inline-flex", flexShrink: 0 }}>
        <Tooltip
          open={isOpen}
          arrow
          placement="bottom-end"
          disableFocusListener
          disableHoverListener
          disableTouchListener
          disableInteractive={false}
          title={
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {presets.length > 0 ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {presetsLabel ? (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {presetsLabel}
                    </Typography>
                  ) : null}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {presets.map((preset) => (
                      <Box
                        key={preset.id}
                        component="button"
                        type="button"
                        disabled={disabled}
                        aria-label={preset.label}
                        onClick={() => applyPreset(preset)}
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          py: 1,
                          cursor: "pointer",
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                          borderRadius: "10px",
                          bgcolor: "#fff",
                          fontFamily: "inherit",
                          "&:hover": { bgcolor: "#F4F4F5" },
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            lineHeight: 1,
                            color: "#111",
                            textShadow: preset.previewShadow,
                          }}
                        >
                          Ag
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ fontSize: 11, color: "text.secondary" }}
                        >
                          {preset.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
              {fields.map((item) => (
                <MetricSliderRow
                  key={item.name}
                  control={control}
                  errors={errors}
                  name={item.name}
                  label={item.label}
                  disabled={disabled}
                  sliderMax={item.sliderMax}
                  onChangeField={item.onChangeField}
                  registerSetter={registerSetter}
                />
              ))}
            </Box>
          }
          componentsProps={{
            tooltip: {
              "data-paint-metric-tooltip": "true",
              sx: tooltipSlotSx,
            },
            arrow: {
              sx: { color: "background.paper" },
            },
          }}
        >
          <span>
            <IconButton
              size="small"
              aria-label={ariaLabel}
              aria-expanded={isOpen}
              aria-haspopup="true"
              disabled={disabled}
              onClick={toggleTooltip}
              sx={{
                width: 40,
                height: 40,
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: BORDER_RADIUS,
                bgcolor: isOpen ? "#F4F4F5" : "#fff",
                color: "text.primary",
              }}
            >
              <SlidersHorizontal size={16} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default PaintMetricSettings;
